import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  Grid,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormGroup,
  FormControlLabel,
  Checkbox,
  
} from '@mui/material';
import {
  Sync as SyncIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  CloudSync as CloudSyncIcon,
  Storage as StorageIcon,
  Grading as GradesIcon,
  People as PeopleIcon,
  HistoryEdu as AdminIcon,
  School as LaureatIcon,
  PhotoCamera as PhotoIcon
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';

const SyncManagement = () => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncStats, setSyncStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [photoStats, setPhotoStats] = useState(null);
  const [isSyncingPhotos, setIsSyncingPhotos] = useState(false);
  const [photoMessage, setPhotoMessage] = useState(null);

  // --- TASK SELECTION STATE ---
  const [selectedTasks, setSelectedTasks] = useState({
    pedago: false,
    pedago_sit: true,
    grades: true,
    students: false,
    admin_sit: false,
    laureats: false,
    validated: false
  });

  const taskLabels = [
    { id: 'pedago', label: 'Structure & Hiérarchie (Elements)', icon: <StorageIcon fontSize="small" /> },
    { id: 'pedago_sit', label: 'Situation Pédagogique Actuelle', icon: <CheckCircleIcon fontSize="small" /> },
    { id: 'grades', label: 'Notes & Résultats (RESULTAT_ELP)', icon: <GradesIcon fontSize="small" /> },
    { id: 'students', label: 'Profils Étudiants', icon: <PeopleIcon fontSize="small" /> },
    { id: 'admin_sit', label: 'Historique Administratif', icon: <AdminIcon fontSize="small" /> },
    { id: 'laureats', label: 'Diplômés (Lauréats)', icon: <LaureatIcon fontSize="small" /> },
    { id: 'validated', label: 'Calcul des Modules Validés', icon: <SyncIcon fontSize="small" /> },
  ];

  const handleToggleTask = (task) => {
    setSelectedTasks(prev => ({ ...prev, [task]: !prev[task] }));
  };
  // ----------------------------

  // Load sync data
  const loadSyncData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const [statusResponse, statsResponse] = await Promise.all([
        adminAPI.getSyncStatus(20),
        adminAPI.getSystemStats(30)
      ]);
      
      setSyncStatus(statusResponse);
      setSyncStats(statsResponse);
      await loadPhotoStats();

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load sync data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSyncData();
    const interval = setInterval(loadSyncData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Trigger manual sync
  const handleManualSync = async () => {
    const tasksToRun = Object.keys(selectedTasks).filter(k => selectedTasks[k]);
    if (tasksToRun.length === 0) {
      setError("Veuillez sélectionner au moins un composant à synchroniser.");
      setConfirmDialog(false);
      return;
    }

    try {
      setIsSyncing(true);
      setError(null);
      setSuccess(null);
      setConfirmDialog(false);
      
      // Pass the selected tasks to the API
      const response = await adminAPI.triggerManualSync(tasksToRun);
      setSuccess(response.message);
      
      setTimeout(() => {
        loadSyncData();
      }, 2000);
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start sync');
    } finally {
      setIsSyncing(false);
    }
  };

  // Load photo stats alongside sync data
  const loadPhotoStats = async () => {
    try {
      const stats = await adminAPI.getPhotoStats();
      setPhotoStats(stats);
    } catch (_) {
      setPhotoStats(null);
    }
  };

  const handlePhotoSync = async () => {
    try {
      setIsSyncingPhotos(true);
      setPhotoMessage(null);
      const response = await adminAPI.syncPhotos();
      setPhotoMessage({ type: 'success', text: response.message });
      setTimeout(loadPhotoStats, 3000);
    } catch (err) {
      setPhotoMessage({ type: 'error', text: err.response?.data?.error || 'Photo sync failed' });
    } finally {
      setIsSyncingPhotos(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('fr-FR');
  };

  const getStatusDisplay = (status) => {
    switch (status) {
      case 'success':
        return { color: 'success', icon: <CheckCircleIcon />, label: 'Success' };
      case 'error':
        return { color: 'error', icon: <ErrorIcon />, label: 'Error' };
      case 'started':
        return { color: 'warning', icon: <WarningIcon />, label: 'In Progress' };
      default:
        return { color: 'default', icon: <SyncIcon />, label: status };
    }
  };

  const getSyncHealthScore = () => {
    if (!syncStatus?.sync_statistics) return 0;
    const stats = syncStatus.sync_statistics;
    let totalSyncs = 0;
    let successfulSyncs = 0;
    stats.forEach(stat => {
      totalSyncs += stat.total_syncs;
      successfulSyncs += stat.successful_syncs;
    });
    return totalSyncs > 0 ? Math.round((successfulSyncs / totalSyncs) * 100) : 0;
  };

  const healthScore = getSyncHealthScore();

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="600" color="primary" gutterBottom>
          🔄 Data Synchronization
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage and monitor data synchronization from Oracle to PostgreSQL
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight="600">
              🚀 Quick Actions
            </Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={loadSyncData}
              disabled={isLoading}
              variant="outlined"
            >
              Refresh Status
            </Button>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={isSyncing ? <CircularProgress size={20} color="inherit" /> : <PlayIcon />}
                onClick={() => setConfirmDialog(true)}
                disabled={isSyncing || isLoading}
                sx={{
                  py: 2,
                  background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #c0392b 0%, #e74c3c 100%)',
                  }
                }}
              >
                {isSyncing ? 'Syncing...' : 'Start Manual Sync'}
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Button fullWidth variant="outlined" size="large" startIcon={<HistoryIcon />} onClick={loadSyncData} disabled={isLoading} sx={{ py: 2 }}>
                View Sync History
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Button fullWidth variant="outlined" size="large" startIcon={<CloudSyncIcon />} disabled={isLoading} sx={{ py: 2 }} color="info">
                Sync Configuration
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── PHOTO SYNC CARD ── */}
      <Card sx={{ mb: 3, borderLeft: '4px solid #9b59b6' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight="600">
                <PhotoIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#9b59b6' }} />
                Photos Étudiants (Oracle → Serveur)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Importe les photos depuis <strong>INDIVIDU.PHOTO_IND</strong> et les enregistre dans <code>uploads/photos/</code>
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={isSyncingPhotos ? <CircularProgress size={18} color="inherit" /> : <PhotoIcon />}
              onClick={handlePhotoSync}
              disabled={isSyncingPhotos}
              sx={{ background: 'linear-gradient(135deg, #9b59b6, #8e44ad)', whiteSpace: 'nowrap' }}
            >
              {isSyncingPhotos ? 'Import en cours…' : 'Importer les Photos'}
            </Button>
          </Box>

          {photoMessage && (
            <Alert severity={photoMessage.type} sx={{ mb: 2 }} onClose={() => setPhotoMessage(null)}>
              {photoMessage.text}
            </Alert>
          )}

          {photoStats && (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                <Chip label={`Total étudiants : ${photoStats.total}`} variant="outlined" />
                <Chip label={`Avec photo : ${photoStats.with_photo}`} color="success" variant="outlined" icon={<CheckCircleIcon />} />
                <Chip label={`Sans photo : ${photoStats.without_photo}`} color="warning" variant="outlined" icon={<WarningIcon />} />
              </Box>
              {photoStats.total > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Couverture : {Math.round((photoStats.with_photo / photoStats.total) * 100)}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.round((photoStats.with_photo / photoStats.total) * 100)}
                    sx={{ mt: 0.5, height: 8, borderRadius: 4, bgcolor: '#f5eef8', '& .MuiLinearProgress-bar': { bgcolor: '#9b59b6' } }}
                  />
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {syncStatus && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="600" gutterBottom>
                  📊 Last Sync Status
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {syncStatus.last_sync ? (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Chip
                        icon={getStatusDisplay(syncStatus.last_sync.sync_status).icon}
                        label={getStatusDisplay(syncStatus.last_sync.sync_status).label}
                        color={getStatusDisplay(syncStatus.last_sync.sync_status).color}
                        sx={{ mr: 2 }}
                      />
                      <Typography variant="body1" fontWeight="500">
                        {syncStatus.last_sync.sync_type}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Records Processed</Typography>
                      <Typography variant="h4" fontWeight="bold" color="primary">
                        {syncStatus.last_sync.records_processed || 0}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Timestamp</Typography>
                      <Typography variant="body1">{formatDate(syncStatus.last_sync.sync_timestamp)}</Typography>
                    </Box>
                    {syncStatus.last_sync.error_message && (
                      <Alert severity="error" sx={{ mt: 2 }}>{syncStatus.last_sync.error_message}</Alert>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body1" color="text.secondary">No sync history available</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="600" gutterBottom>❤️ Sync Health Score</Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography variant="h2" fontWeight="bold" color={healthScore >= 90 ? 'success.main' : healthScore >= 70 ? 'warning.main' : 'error.main'}>
                    {healthScore}%
                  </Typography>
                  <Typography variant="body1" color="text.secondary">Success Rate (Last 30 Days)</Typography>
                </Box>
                <LinearProgress variant="determinate" value={healthScore} sx={{ height: 10, borderRadius: 1, mb: 2, '& .MuiLinearProgress-bar': { backgroundColor: healthScore >= 90 ? '#4caf50' : healthScore >= 70 ? '#ff9800' : '#f44336' } }} />
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {healthScore >= 90 ? 'Excellent' : healthScore >= 70 ? 'Good' : 'Needs Attention'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Sync Statistics table remains unchanged */}

      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <WarningIcon color="warning" />
            <Typography variant="h6">Sélectionner les composants à synchroniser</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
            Choisissez les données spécifiques à mettre à jour depuis Oracle Apogée.
          </Typography>
          
          <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fbfbfb' }}>
            <FormGroup>
              {taskLabels.map((task) => (
                <FormControlLabel
                  key={task.id}
                  control={<Checkbox size="small" checked={selectedTasks[task.id]} onChange={() => handleToggleTask(task.id)} />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {task.icon}
                      <Typography variant="body2">{task.label}</Typography>
                    </Box>
                  }
                />
              ))}
            </FormGroup>
          </Paper>

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="error">
              * Ce processus mettra à jour directement la base de données PostgreSQL.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)} disabled={isSyncing}>Annuler</Button>
          <Button 
            onClick={handleManualSync} 
            variant="contained" 
            color="warning" 
            disabled={isSyncing || Object.values(selectedTasks).every(v => !v)}
            startIcon={isSyncing ? <CircularProgress size={20} /> : <PlayIcon />}
          >
            {isSyncing ? 'Lancement...' : 'Démarrer la Synchronisation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SyncManagement;